import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";
import InfoTip from "../shared/InfoTip";
import PanelTakeaway from "../shared/PanelTakeaway";
import {
  getCdpChannelPerformance,
  getCdpChannelCqs,
  getCdpAttribution,
  getCdpAttributionPaths,
  getCdpFacebookDeepDive,
} from "../../api";

/* ───────────── channel colors ───────────── */
const CH_HEX = {
  "Paid Search": "#2563EB", "Paid Social": "#7C3AED", "Organic Search": "#059669",
  "Organic Social": "#0D9488", "Direct": "#6B7280", "Maps": "#D97706",
  "Email": "#DB2777", "AI": "#4F46E5", "Referral": "#B45309",
  "Booking Platform": "#0891B2", "Display": "#DC2626", "Affiliate": "#65A30D",
};
const CH_PILL = {
  "Paid Search": "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "Paid Social": "bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300",
  "Organic Search": "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  "Organic Social": "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "Direct": "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",
  "Maps": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  "Email": "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  "AI": "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300",
  "Referral": "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "Booking Platform": "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300",
  "Display": "bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-300",
  "Affiliate": "bg-lime-100 text-lime-700 dark:bg-lime-900/40 dark:text-lime-300",
};
const DEFAULT_PILL = "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400";

function fmtTime(secs) {
  if (!secs || secs <= 0) return "0s";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? (s > 0 ? `${m}m ${s}s` : `${m}m`) : `${s}s`;
}

function fmtDollar(v) {
  if (v == null) return "\u2014";
  return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const FB_COLORS = ["#7C3AED", "#059669", "#2563EB", "#D97706", "#6B7280"];

/* ═══════════════════ COMPONENT ═══════════════════ */
export default function CdpChannelsTab({ clientId, rangeStart, rangeEnd, includeBots = false, isDarkMode, say }) {
  const [perf, setPerf] = useState([]);
  const [perfLoading, setPerfLoading] = useState(false);
  const [cqs, setCqs] = useState([]);
  const [cqsLoading, setCqsLoading] = useState(false);
  const [attr, setAttr] = useState([]);
  const [attrLoading, setAttrLoading] = useState(false);
  const [paths, setPaths] = useState([]);
  const [pathsLoading, setPathsLoading] = useState(false);
  const [fb, setFb] = useState(null);
  const [fbLoading, setFbLoading] = useState(false);

  useEffect(() => {
    if (!clientId || !rangeStart || !rangeEnd) return;

    // Each setter coerces to the shape its state expects so the render code
    // (which reads .length / .map / .reduce on perf / cqs / attr / paths) never
    // sees null. fb is an object (or null) and is always accessed with
    // `fb &&` / `fb?.`, so it stays as-is. Previously the helper passed `null`
    // when an RPC returned no data, and `perf.length` then crashed the tab.
    const load = async (fn, setter, loadSetter) => {
      try {
        loadSetter(true);
        const resp = await fn(clientId, rangeStart, rangeEnd, { includeBots });
        setter(resp?.data ?? null);
      } catch (e) {
        console.error(e);
        setter(null);
      } finally {
        loadSetter(false);
      }
    };

    const arr = (d) => (Array.isArray(d) ? d : []);
    load(getCdpChannelPerformance, (d) => setPerf(arr(d)), setPerfLoading);
    load(getCdpChannelCqs, (d) => setCqs(arr(d)), setCqsLoading);
    load(getCdpAttribution, (d) => setAttr(arr(d)), setAttrLoading);
    load(getCdpAttributionPaths, (d) => setPaths(arr(d)), setPathsLoading);
    load(getCdpFacebookDeepDive, (d) => setFb(d ?? null), setFbLoading);
  }, [clientId, rangeStart, rangeEnd, includeBots]);

  const maxVisitors = perf.length > 0 ? Math.max(...perf.map(p => p.visitors)) : 1;
  const avgBookRate = perf.length > 0 ? perf.reduce((sum, p) => sum + (p.book_click_rate || 0), 0) / perf.length : 0;
  const hasFbData = fb && fb.total_fb_visitors > 0;
  const multiChannelPct = paths.length > 0
    ? Math.round(paths.reduce((s, p) => s + (p.bookings || 0), 0) / Math.max(1, attr.reduce((s, a) => s + (a.bookings || 0), 0)) * 100)
    : 0;

  return (
    <div className="space-y-6">

      {/* ═══ 4A: CHANNEL PERFORMANCE TABLE ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <PanelTakeaway panelKey="channels" say={say} />
          <h3 className="gi-panel-title text-gray-900 dark:text-white">Channel Performance</h3>
          {/* P0-2 (punch list): clarify that per-channel visitors may overlap (multi-channel visitors counted once per channel) so the sum doesn't drift silently from the canonical KPI total. */}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Visitors counted per channel — a visitor who used multiple channels is counted in each. The sum will exceed the canonical Visitors KPI.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Channel</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400" title="Visitors per channel — multi-channel visitors counted in each channel they touched.">Visitors</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Sessions</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Pages/Session</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Avg Time</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Book Clicks</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Bookings</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400" title="Booking revenue we can trace to this channel. Channel-attributed revenue covers ~half of total revenue (cv-linked bookings); the rest is unattributed. Channel totals will NOT sum to the Revenue KPI.">Revenue</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Phone</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Book Rate</th>
              </tr>
            </thead>
            <tbody>
              {perfLoading ? (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : perf.length === 0 ? (
                <tr><td colSpan={10} className="text-center py-8 text-gray-400 text-sm">No channel data</td></tr>
              ) : perf.map((r, i) => {
                // P1-1 (punch list 2026-06-05): badge low-volume rows so a "30% book rate"
                // from 3 visitors doesn't read like a trend. Threshold mirrors the punch list spec.
                const lowVolume = (r.visitors || 0) < 10;
                return (
                <tr key={r.channel} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""} ${lowVolume ? "opacity-60" : ""}`}>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${CH_PILL[r.channel] || DEFAULT_PILL}`}>{r.channel}</span>
                      {lowVolume && (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300" title="Under 10 visitors — rates here are noisy and not yet directional.">Low volume</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gray-900 dark:text-white w-10 text-right">{r.visitors}</span>
                      <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(r.visitors / maxVisitors) * 100}%`, backgroundColor: CH_HEX[r.channel] || "#6B7280" }}></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.sessions}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.pages_per_session}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-600 dark:text-gray-400">{fmtTime(r.avg_time_seconds)}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.book_clicks}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.bookings}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.revenue ? `$${Math.round(r.revenue).toLocaleString()}` : "—"}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.phone_clicks}</td>
                  <td className={`px-4 py-2.5 text-right text-xs font-medium ${lowVolume ? "text-gray-400 dark:text-gray-500" : r.book_click_rate > avgBookRate ? "text-emerald-600 dark:text-emerald-400" : r.book_click_rate < avgBookRate && r.book_click_rate > 0 ? "text-red-500 dark:text-red-400" : "text-gray-500 dark:text-gray-400"}`}>
                    {Math.min(r.book_click_rate || 0, 100).toFixed(1)}%
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ 4B: CHANNEL QUALITY SCORE ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <h3 className="gi-panel-title text-gray-900 dark:text-white mb-1">Channel Quality Score</h3>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">Combines engagement depth, conversion rate, return visits, and interaction rate into a single 0–100 metric.</p>
        {cqsLoading ? (
          <div className="h-48 flex items-center justify-center text-gray-400">Loading...</div>
        ) : cqs.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-gray-400 text-sm">Not enough data (min 5 visitors per channel)</div>
        ) : (
          <ResponsiveContainer width="100%" height={cqs.length * 44 + 20}>
            <BarChart data={cqs} layout="vertical" margin={{ top: 0, right: 50, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={isDarkMode ? "#374151" : "#E5E7EB"} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
              <YAxis dataKey="channel" type="category" width={120} tick={{ fontSize: 11 }} stroke={isDarkMode ? "#6B7280" : "#9CA3AF"} />
              <Tooltip
                contentStyle={{ backgroundColor: isDarkMode ? "#1F2937" : "#fff", border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`, borderRadius: 8, fontSize: 12 }}
                formatter={(v) => [`${v}`, "CQS"]}
              />
              <Bar dataKey="cqs" radius={[0, 4, 4, 0]} barSize={20}>
                {cqs.map((entry) => (
                  <Cell key={entry.channel} fill={CH_HEX[entry.channel] || "#6B7280"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ═══ 4C: SOURCE-TO-STAY ATTRIBUTION ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <PanelTakeaway panelKey="attribution" say={say} />
          <h3 className="gi-panel-title text-gray-900 dark:text-white">Source-to-Stay Attribution</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">First-touch channel that introduced a guest, linked to their booking value.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">First Touch</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Last Touch</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Bookers</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Bookings</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Revenue</th>
                <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Avg Value</th>
              </tr>
            </thead>
            <tbody>
              {attrLoading ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : attr.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8 text-gray-400 text-sm">No attribution data yet</td></tr>
              ) : attr.map((r, i) => (
                <tr key={i} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""}`}>
                  <td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${CH_PILL[r.first_channel] || DEFAULT_PILL}`}>{r.first_channel || "\u2014"}</span></td>
                  <td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${CH_PILL[r.last_channel] || DEFAULT_PILL}`}>{r.last_channel || "\u2014"}</span></td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.bookers}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.bookings}</td>
                  <td className="px-4 py-2.5 text-right text-xs font-medium text-gray-900 dark:text-white">{fmtDollar(r.total_revenue)}</td>
                  <td className="px-4 py-2.5 text-right text-xs text-gray-600 dark:text-gray-400">{fmtDollar(r.avg_booking_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ 4D: CROSS-SESSION ATTRIBUTION PATHS ═══ */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
          <h3 className="gi-panel-title text-gray-900 dark:text-white">Cross-Session Attribution Paths</h3>
          {paths.length > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {multiChannelPct > 0 ? `${multiChannelPct}% of converters used more than one channel before booking.` : "Multi-touch paths for visitors who used more than one channel."}
            </p>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400 w-24">Touchpoints</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">First Touch</th>
                <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Converting Touch</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Visitors</th>
                <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Bookings</th>
              </tr>
            </thead>
            <tbody>
              {pathsLoading ? (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : paths.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">No multi-touch paths found</td></tr>
              ) : paths.map((r, i) => (
                <tr key={i} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""}`}>
                  <td className="px-4 py-2.5 text-center text-xs font-medium text-gray-700 dark:text-gray-300">{r.touchpoints}</td>
                  <td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${CH_PILL[r.first_channel] || DEFAULT_PILL}`}>{r.first_channel || "\u2014"}</span></td>
                  <td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${CH_PILL[r.converting_channel] || DEFAULT_PILL}`}>{r.converting_channel || "\u2014"}</span></td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.visitors}</td>
                  <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.bookings}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ 4E: FACEBOOK DEEP-DIVE ═══ */}
      {hasFbData && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-800">
            <h3 className="gi-panel-title text-gray-900 dark:text-white">Facebook / Paid Social Deep-Dive <InfoTip text="Visitor count here may differ from the Channels table above. The Channels table counts all Paid Social traffic; this deep-dive counts only Facebook-sourced visitors — a subset of Paid Social." /></h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{fb.total_fb_visitors} visitors from Paid Social this period</p>
          </div>

          {/* Ad Performance Table */}
          {fb.ad_performance?.length > 0 && (
            <div className="overflow-x-auto border-b border-gray-200 dark:border-gray-800">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Campaign</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Visitors</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Sessions</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Book Clicks</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Bookings</th>
                    <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Phone</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-gray-500 dark:text-gray-400">Book Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {fb.ad_performance.slice(0, 20).map((r, i) => {
                    // P1-1: low-volume row badging — FB campaigns with <10 visitors are noisy.
                    const lowVolume = (r.visitors || 0) < 10;
                    return (
                    <tr key={i} className={`border-b border-gray-100 dark:border-gray-800 ${i % 2 === 1 ? "bg-gray-50/50 dark:bg-gray-900/50" : ""} ${lowVolume ? "opacity-60" : ""}`}>
                      <td className="px-4 py-2.5 text-xs text-gray-700 dark:text-gray-300 truncate max-w-[260px]" title={r.campaign_id}>
                        <span>{r.campaign_id}</span>
                        {lowVolume && (
                          <span className="ml-1.5 inline-block px-1.5 py-0.5 rounded text-[9px] font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300" title="Under 10 visitors — rates here are not yet reliable.">Low</span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.visitors}</td>
                      <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.sessions}</td>
                      <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.book_clicks}</td>
                      <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.bookings}</td>
                      <td className="px-4 py-2.5 text-center text-xs text-gray-700 dark:text-gray-300">{r.phone_clicks}</td>
                      <td className={`px-4 py-2.5 text-right text-xs font-medium ${lowVolume ? "text-gray-400 dark:text-gray-500" : "text-gray-700 dark:text-gray-300"}`}>{Math.min(r.book_click_rate || 0, 100).toFixed(1)}%</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Engagement Breakdown — Donut + Table */}
          {fb.engagement?.length > 0 && (
            <div className="px-5 py-4">
              <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">Engagement Breakdown</h4>
              <div className="flex items-start gap-6">
                <div className="w-40 h-40 shrink-0">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={fb.engagement} dataKey="visitors" nameKey="segment" innerRadius={35} outerRadius={65} paddingAngle={2}>
                        {fb.engagement.map((entry, idx) => (
                          <Cell key={entry.segment} fill={FB_COLORS[idx % FB_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: isDarkMode ? "#1F2937" : "#fff", border: `1px solid ${isDarkMode ? "#374151" : "#E5E7EB"}`, borderRadius: 8, fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-1.5">
                  {fb.engagement.map((s, idx) => (
                    <div key={s.segment} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: FB_COLORS[idx % FB_COLORS.length] }}></span>
                      <span className="text-xs text-gray-700 dark:text-gray-300 flex-1">{s.segment}</span>
                      <span className="text-xs font-medium text-gray-900 dark:text-white">{s.visitors}</span>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500 w-10 text-right">{s.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {fbLoading && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center text-gray-400">
          Loading Facebook data...
        </div>
      )}
    </div>
  );
}
